import { useEffect, useRef } from 'react';
import { useMediaSessions } from '@/hooks/useMediaSessions';

/**
 * Persistent Audio Layer - manages audio playback for all active media sessions
 * 
 * This component lives at the app root level and NEVER unmounts during tab navigation.
 * It renders invisible <audio> elements for each active session, ensuring audio
 * continues playing regardless of which tab/panel the user is viewing.
 * 
 * Architecture benefits:
 * - Audio continues during navigation (conversations, groups, streams tabs)
 * - Supports group calls where user chats in text while audio continues
 * - Decouples audio playback from visual UI (StreamWindow becomes visual-only)
 * - Properly handles late joiners to group calls
 * - Supports multiple concurrent streams (future)
 */
export function PersistentAudioLayer() {
  const mediaSessions = useMediaSessions();
  
  // Render audio for active AND connecting sessions
  // CRITICAL: Keep audio element mounted during 'connecting' state (channel recovery)
  // This prevents audio death when AV channel closes/reopens during CPU pressure
  // Video survives because StreamWindow doesn't unmount on state change - audio should behave the same
  const activeSessions = mediaSessions.filter(s => 
    s.session.state === 'active' || s.session.state === 'connecting'
  );
  
  return (
    <div 
      id="persistent-audio-layer" 
      style={{ 
        position: 'absolute', 
        width: 0, 
        height: 0, 
        overflow: 'hidden',
        pointerEvents: 'none' 
      }}
      aria-hidden="true"
    >
      {activeSessions.map(({ sessionId, session, peerAddress }) => (
        <PersistentAudioElement
          key={`${sessionId}-${peerAddress}`}
          sessionId={sessionId}
          session={session}
          peerAddress={peerAddress}
        />
      ))}
    </div>
  );
}

interface PersistentAudioElementProps {
  sessionId: string;
  session: any; // MediaSession
  peerAddress: string;
}

/**
 * Individual audio element for a single remote stream
 * Handles stream attachment and playback independently of UI components
 */
function PersistentAudioElement({ sessionId, session, peerAddress }: PersistentAudioElementProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const lastStreamIdRef = useRef<string | null>(null);
  const mutedRef = useRef(false);
  
  // Listen for mute toggle events from StreamWindow UI
  useEffect(() => {
    const handleMuteToggle = (event: CustomEvent) => {
      if (event.detail.sessionId === sessionId && event.detail.peerAddress === peerAddress) {
        mutedRef.current = event.detail.muted;
        const audioElement = audioRef.current;
        if (audioElement) {
          audioElement.muted = event.detail.muted;
          console.log('[PERSISTENT_AUDIO] Speaker mute toggled', {
            sessionId: sessionId.slice(0, 8),
            peer: peerAddress.slice(0, 8),
            muted: event.detail.muted
          });
        }
      }
    };
    
    window.addEventListener('av-speaker-mute-toggle', handleMuteToggle as EventListener);
    return () => {
      window.removeEventListener('av-speaker-mute-toggle', handleMuteToggle as EventListener);
    };
  }, [sessionId, peerAddress]);
  
  // Attach remote stream to audio element
  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) return;
    
    const remoteStream = session.remoteStreams?.get(peerAddress);
    
    if (remoteStream) {
      // AV FIX 5: Guard against attaching streams whose audio tracks are all ended.
      // This can happen after call teardown races or OS permission revocation.
      const audioTracks = remoteStream.getAudioTracks();
      const hasLiveAudio = audioTracks.length > 0 && audioTracks.some((t: MediaStreamTrack) => t.readyState === 'live');
      if (audioTracks.length > 0 && !hasLiveAudio) {
        console.warn('[PERSISTENT_AUDIO] Skipping attach — all audio tracks ended', {
          sessionId: sessionId.slice(0, 8),
          peer: peerAddress.slice(0, 8),
          trackCount: audioTracks.length
        });
        return;
      }

      // Check if stream has actually changed (avoid unnecessary reattachment)
      // BUT: after ICE recovery the stream ID is the same yet playback may be paused.
      // Only skip if stream is already attached AND playing.
      const streamId = remoteStream.id;
      if (lastStreamIdRef.current === streamId && audioElement.srcObject === remoteStream && !audioElement.paused) {
        return; // Already attached and playing
      }
      
      lastStreamIdRef.current = streamId;
      audioElement.srcObject = remoteStream;
      
      console.log('[PERSISTENT_AUDIO] Attaching remote stream', {
        sessionId: sessionId.slice(0, 8),
        peer: peerAddress.slice(0, 8),
        trackCount: remoteStream.getTracks().length,
        audioTracks: remoteStream.getAudioTracks().length
      });
      
      // Preserve muted state across stream re-attachment
      audioElement.muted = mutedRef.current;

      // Play audio - this should work since user has already interacted (accepted call)
      audioElement.play().catch(err => {
        console.error('[PERSISTENT_AUDIO] Play failed', {
          sessionId: sessionId.slice(0, 8),
          peer: peerAddress.slice(0, 8),
          error: err.message
        });
      });
    }
  }, [session, sessionId, peerAddress, session.remoteStreams]);
  
  // Track mute/unmute event handling for extended mute recovery
  // This is critical for when remote peer mutes their mic for extended periods
  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) return;
    
    const remoteStream = session.remoteStreams?.get(peerAddress);
    if (!remoteStream) return;
    
    const audioTracks = remoteStream.getAudioTracks();
    if (audioTracks.length === 0) return;
    
    // Retry play with exponential backoff
    const attemptPlay = (retryCount: number = 0) => {
      if (!audioElement.paused) return; // Already playing
      
      audioElement.play().catch(err => {
        if (err.name === 'AbortError' && retryCount < 3) {
          console.log('[PERSISTENT_AUDIO] Play interrupted on unmute, retrying...', {
            sessionId: sessionId.slice(0, 8),
            retry: retryCount + 1
          });
          setTimeout(() => attemptPlay(retryCount + 1), 100 * (retryCount + 1));
        } else if (err.name !== 'AbortError') {
          console.error('[PERSISTENT_AUDIO] Play failed on unmute', {
            sessionId: sessionId.slice(0, 8),
            error: err.message
          });
        }
      });
    };
    
    // Handler for track unmute - remote peer re-enabled their mic
    const handleUnmute = (event: Event) => {
      const track = event.target as MediaStreamTrack;
      console.log('[PERSISTENT_AUDIO] Audio track unmuted - re-triggering playback', {
        sessionId: sessionId.slice(0, 8),
        peer: peerAddress.slice(0, 8),
        trackId: track.id,
        trackReadyState: track.readyState,
        trackEnabled: track.enabled
      });
      
      // Re-trigger play after unmute
      attemptPlay();
    };
    
    // Handler for track mute - remote peer disabled their mic
    const handleMute = (event: Event) => {
      const track = event.target as MediaStreamTrack;
      console.log('[PERSISTENT_AUDIO] Audio track muted by remote peer', {
        sessionId: sessionId.slice(0, 8),
        peer: peerAddress.slice(0, 8),
        trackId: track.id,
        trackReadyState: track.readyState
      });
    };
    
    // Handler for track ended - track was stopped entirely
    const handleEnded = (event: Event) => {
      const track = event.target as MediaStreamTrack;
      console.warn('[PERSISTENT_AUDIO] Audio track ended unexpectedly', {
        sessionId: sessionId.slice(0, 8),
        peer: peerAddress.slice(0, 8),
        trackId: track.id
      });
    };
    
    // Attach listeners to all audio tracks
    audioTracks.forEach((track: MediaStreamTrack) => {
      track.addEventListener('unmute', handleUnmute);
      track.addEventListener('mute', handleMute);
      track.addEventListener('ended', handleEnded);
      
      console.log('[PERSISTENT_AUDIO] Attached track event listeners', {
        sessionId: sessionId.slice(0, 8),
        trackId: track.id,
        initialMuted: track.muted,
        initialEnabled: track.enabled,
        readyState: track.readyState
      });
    });
    
    // Cleanup listeners on unmount or stream change
    return () => {
      audioTracks.forEach((track: MediaStreamTrack) => {
        track.removeEventListener('unmute', handleUnmute);
        track.removeEventListener('mute', handleMute);
        track.removeEventListener('ended', handleEnded);
      });
    };
  }, [session.remoteStreams, sessionId, peerAddress]);
  
  // Listen for remote stream added events (in case stream arrives after mount)
  // AND av-session-reconnected (ICE self-recovery — same stream, playback interrupted)
  useEffect(() => {
    const forceReattach = (label: string) => {
      const audioElement = audioRef.current;
      const remoteStream = session.remoteStreams?.get(peerAddress);
      
      if (audioElement && remoteStream) {
        // AV FIX 5: Guard against re-attaching streams whose audio tracks are all ended.
        const audioTracks = remoteStream.getAudioTracks();
        const hasLiveAudio = audioTracks.length > 0 && audioTracks.some((t: MediaStreamTrack) => t.readyState === 'live');
        if (audioTracks.length > 0 && !hasLiveAudio) {
          console.warn(`[PERSISTENT_AUDIO] ${label} — skipping, all audio tracks ended`, {
            sessionId: sessionId.slice(0, 8),
            peer: peerAddress.slice(0, 8)
          });
          return;
        }

        // Always re-set srcObject — after ICE recovery the browser may have
        // internally paused the audio even though the track is still live.
        audioElement.srcObject = remoteStream;
        audioElement.muted = mutedRef.current;
        lastStreamIdRef.current = remoteStream.id;
        
        console.log(`[PERSISTENT_AUDIO] ${label} — re-attaching stream`, {
          sessionId: sessionId.slice(0, 8),
          peer: peerAddress.slice(0, 8),
          liveTracks: remoteStream.getTracks().filter((t: MediaStreamTrack) => t.readyState === 'live').length
        });
        
        const attemptPlay = (retryCount: number = 0) => {
          audioElement.play().catch(err => {
            if (err.name === 'AbortError' && retryCount < 3) {
              console.log('[PERSISTENT_AUDIO] Play interrupted, retrying...', {
                sessionId: sessionId.slice(0, 8),
                retry: retryCount + 1
              });
              setTimeout(() => attemptPlay(retryCount + 1), 100);
            } else if (err.name !== 'AbortError') {
              console.error(`[PERSISTENT_AUDIO] Play failed after ${label}`, {
                sessionId: sessionId.slice(0, 8),
                error: err.message
              });
            }
          });
        };
        attemptPlay();
      }
    };

    const handleRemoteStreamAdded = (event: CustomEvent) => {
      if (event.detail.sessionId === sessionId && event.detail.peerAddress === peerAddress) {
        forceReattach('Remote stream added');
      }
    };
    
    // AV FIX A: Also handle ICE self-recovery reconnect events
    const handleSessionReconnected = (event: CustomEvent) => {
      if (event.detail.sessionId === sessionId) {
        forceReattach('Session reconnected (ICE recovery)');
      }
    };
    
    window.addEventListener('av-remote-stream-added', handleRemoteStreamAdded as EventListener);
    window.addEventListener('av-session-reconnected', handleSessionReconnected as EventListener);
    return () => {
      window.removeEventListener('av-remote-stream-added', handleRemoteStreamAdded as EventListener);
      window.removeEventListener('av-session-reconnected', handleSessionReconnected as EventListener);
    };
  }, [sessionId, peerAddress, session.remoteStreams]);
  
  // Periodic audio health check - fallback for edge cases where events don't fire
  // Checks every 5 seconds if audio should be playing but isn't
  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) return;
    
    const healthCheck = setInterval(() => {
      const remoteStream = session.remoteStreams?.get(peerAddress);
      if (!remoteStream) return;
      
      const audioTracks = remoteStream.getAudioTracks();
      if (audioTracks.length === 0) return;
      
      const track = audioTracks[0];
      const shouldBePlaying = track.readyState === 'live' && !track.muted && track.enabled;
      const isPlaying = !audioElement.paused && !audioElement.ended;
      
      // If track is live and unmuted but audio isn't playing, attempt recovery
      if (shouldBePlaying && !isPlaying) {
        console.log('[PERSISTENT_AUDIO] Health check: audio should be playing but isn\'t - recovering', {
          sessionId: sessionId.slice(0, 8),
          peer: peerAddress.slice(0, 8),
          trackReadyState: track.readyState,
          trackMuted: track.muted,
          trackEnabled: track.enabled,
          audioPaused: audioElement.paused,
          audioEnded: audioElement.ended
        });
        
        // Re-attach stream and play, preserving muted state
        audioElement.srcObject = remoteStream;
        audioElement.muted = mutedRef.current;
        audioElement.play().catch(err => {
          console.error('[PERSISTENT_AUDIO] Health check recovery play failed', {
            sessionId: sessionId.slice(0, 8),
            error: err.message
          });
        });
      }
    }, 5000);
    
    return () => clearInterval(healthCheck);
  }, [session.remoteStreams, sessionId, peerAddress]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const audioElement = audioRef.current;
      if (audioElement) {
        audioElement.srcObject = null;
        console.log('[PERSISTENT_AUDIO] Cleanup - detached stream', {
          sessionId: sessionId.slice(0, 8),
          peer: peerAddress.slice(0, 8)
        });
      }
    };
  }, [sessionId, peerAddress]);
  
  return (
    <audio
      ref={audioRef}
      autoPlay
      playsInline
      data-session-id={sessionId}
      data-peer-address={peerAddress}
    />
  );
}
